import { ArticleType } from '@lib/article';
import { DeliveryReceiptArticle } from './DeliveryReceiptArticle';
import { DeliveryReceiptArticleSimple } from './DeliveryReceiptArticleSimple';
import { MatDialog } from '@angular/material/dialog';
import {
    InsertIntoWarehouseDialogComponent,
    InsertIntoWarehouseData,
    InsertIntoWarehouseResult,
    IStorageCandidate,
    ScanToInsertDialogData,
    WarehouseScanToInsertComponent,
} from '@lib/warehouse';
import { ProductUnit } from '@lib/product';
import { DialogOptions, EprocBarcodeScannerOptions } from '@lib/util';
import { BarcodeScanner } from '@awesome-cordova-plugins/barcode-scanner/ngx';
import { from, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { BarcodeScanResult } from '@awesome-cordova-plugins/barcode-scanner';
import { UriBuilder } from 'uribuilder';
import { QrCodeIdentifier } from '@lib/qr-code';

export interface IDeliveryReceiptItem {
    id: number;
    name: string;
    articleNumberSupplier: string;
    customerArticleSku: string;
    manufacturerArticleSku: string;
    shortDescription: string;
    tableName: ArticleType;
    manufacturer: string;
    positionReceipt: number;
    positionOrder: number;
    orderItemId: number;
    quantity: string;
    orderNumber: string;
    erpOrderNumber?: string | null;
    note: string;
    orderUnit: string; // unece
    contentUnit: string; // unece
    numberOfContentUnitsPerOrderUnit: number;
    quantityStocked: number;
    supplierCompanyId: string;
    deliveryCompleted: boolean;
}

export class DeliveryReceiptItem implements IDeliveryReceiptItem {
    readonly articleNumberSupplier: string;
    readonly contentUnit: string;
    readonly customerArticleSku: string;
    readonly id: number;
    readonly manufacturer: string;
    readonly manufacturerArticleSku: string;
    readonly name: string;
    readonly note: string;
    readonly numberOfContentUnitsPerOrderUnit: number;
    readonly orderItemId: number;
    readonly orderNumber: string;
    readonly erpOrderNumber: string;
    readonly orderUnit: string;
    readonly positionOrder: number;
    readonly positionReceipt: number;
    readonly quantity: string;
    readonly shortDescription: string;
    readonly tableName: ArticleType;
    readonly supplierCompanyId: string;
    readonly deliveryCompleted: boolean;
    quantityStocked: number;

    readonly article: DeliveryReceiptArticle;
    readonly articleSimple: DeliveryReceiptArticle;

    public get storageCandidate(): IStorageCandidate {
        return {
            name: this.name,
            description: this.shortDescription,
            articleNumberCustomer: this.customerArticleSku,
            manufacturerName: this.manufacturer,
            manufacturerArticleNumber: this.manufacturerArticleSku,
            supplierCompanyId: this.supplierCompanyId,
            supplierArticleNumber: this.articleNumberSupplier,
            units: new ProductUnit(this.contentUnit, this.numberOfContentUnitsPerOrderUnit, this.orderUnit),
            quantity: +this.quantity,
            receipt: {
                receiptItemId: this.id,
                receiptId: this.receiptId,
                orderItemId: this.orderItemId,
            },
        };
    }

    constructor(
        dto: IDeliveryReceiptItem,
        private readonly receiptId: number,
        private readonly dialog: MatDialog,
        private readonly barcodeScanner: BarcodeScanner,
    ) {
        this.articleNumberSupplier = dto.articleNumberSupplier;
        this.contentUnit = dto.contentUnit;
        this.customerArticleSku = dto.customerArticleSku;
        this.id = dto.id;
        this.manufacturer = dto.manufacturer;
        this.manufacturerArticleSku = dto.manufacturerArticleSku;
        this.name = dto.name;
        this.note = dto.note;
        this.numberOfContentUnitsPerOrderUnit = dto.numberOfContentUnitsPerOrderUnit;
        this.orderItemId = dto.orderItemId;
        this.orderNumber = dto.orderNumber;
        this.erpOrderNumber = dto.erpOrderNumber ?? '';
        this.orderUnit = dto.orderUnit;
        this.positionOrder = dto.positionOrder;
        this.positionReceipt = dto.positionReceipt;
        this.quantity = dto.quantity;
        this.shortDescription = dto.shortDescription;
        this.tableName = dto.tableName;
        this.quantityStocked = dto.quantityStocked;
        this.supplierCompanyId = dto.supplierCompanyId;
        this.deliveryCompleted = dto.deliveryCompleted ?? false;
        this.article = DeliveryReceiptArticle.fromDeliveryReceiptItem(this);
        this.articleSimple = DeliveryReceiptArticleSimple.fromDeliveryReceiptItem(this);
    }

    public get displayOrderNumber(): string {
        const erpOrderNumber = this.erpOrderNumber.trim();

        if (erpOrderNumber.length > 0) {
            return erpOrderNumber;
        }

        return this.orderNumber;
    }

    public addToStorage(): void {
        const data = new InsertIntoWarehouseData(this.storageCandidate, this.quantityStocked);
        const options = DialogOptions.MEDIUM(data);
        options.autoFocus = false;
        this.dialog
            .open(InsertIntoWarehouseDialogComponent, options)
            .afterClosed()
            .subscribe((result: InsertIntoWarehouseResult | undefined) => {
                if (result?.newStock !== undefined) {
                    this.quantityStocked = result.newStock;
                }
            });
    }

    public scanToInsertIntoStorage(): void {
        from(this.barcodeScanner.scan(new EprocBarcodeScannerOptions()))
            .pipe(
                map((scanResult: BarcodeScanResult) => {
                    if (!scanResult.cancelled) {
                        const uri = UriBuilder.parse(scanResult.text);
                        if (uri.query[QrCodeIdentifier.WAREHOUSE_LEVEL]) {
                            return uri.query[QrCodeIdentifier.WAREHOUSE_LEVEL] as string;
                        }
                    }

                    return '';
                }),
                switchMap((levelId: string) => {
                    if (levelId) {
                        const data = new ScanToInsertDialogData(this.storageCandidate, this.quantityStocked, levelId);
                        const options = DialogOptions.LARGE(data);
                        options.autoFocus = false;
                        return this.dialog.open(WarehouseScanToInsertComponent, options).afterClosed();
                    } else {
                        return of(new InsertIntoWarehouseResult(this.quantityStocked));
                    }
                }),
            )
            .subscribe((result: InsertIntoWarehouseResult | undefined) => {
                if (result) {
                    this.quantityStocked = result.newStock;
                }
            });
    }
}
